import { Pipe } from '@kaoto/camel-catalog/types';
import { isDefined } from '@kaoto/forms';

import { getCamelRandomId } from '../../../camel-utils/camel-random-id';
import { DynamicCatalogRegistry } from '../../../dynamic-catalog/dynamic-catalog-registry';
import {
  getArrayProperty,
  getCustomSchemaFromPipe,
  getValue,
  setValue,
  updatePipeFromCustomSchema,
} from '../../../utils';
import { DefinedComponent } from '../../camel/camel-catalog-index';
import { CatalogKind } from '../../catalog-kind';
import { EntityType } from '../../entities';
import { PipeStep } from '../../entities/pipe-overrides';
import { KaotoSchemaDefinition } from '../../kaoto-schema';
import { PlaceholderType } from '../../placeholder.constants';
import {
  AddStepMode,
  BaseVisualEntity,
  IVisualizationNode,
  IVisualizationNodeData,
  IVisualizationNodeIds,
  NodeInteraction,
} from '../base-visual-entity';
import { IClipboardContent } from '../clipboard';
import { NodeIdentity } from '../node-identity';
import { createVisualizationNode } from '../visualization-node';
import { NodeEnrichmentService } from './nodes/node-enrichment.service';
import { KameletSchemaService } from './support/kamelet-schema.service';
import { ModelValidationService } from './support/validators/model-validation.service';

export class PipeVisualEntity implements BaseVisualEntity {
  id: string;
  readonly type: EntityType = EntityType.Pipe;
  static readonly ROOT_PATH = 'pipe';

  constructor(public pipe: Pipe) {
    this.id = (pipe.metadata?.name as string) ?? getCamelRandomId('pipe');

    this.pipe.metadata = pipe.metadata ?? {};
    this.pipe.metadata.name = this.id;
    this.pipe.spec = pipe.spec ?? {
      source: {},
      steps: [],
      sink: {},
    };
  }

  getRootPath(): string {
    return PipeVisualEntity.ROOT_PATH;
  }

  /** Internal API methods */
  getId(): string {
    return this.id;
  }

  setId(routeId: string): void {
    this.id = routeId;
    this.pipe.metadata!.name = this.id;
  }

  getNodeLabel(path?: string): string {
    if (!path) return '';

    if (path === this.getRootPath()) {
      return this.id;
    }

    const stepModel: PipeStep = getValue(this.pipe.spec, path);
    return KameletSchemaService.getNodeLabel(stepModel, path);
  }

  async fetchNodeSchema(ids: IVisualizationNodeIds): Promise<KaotoSchemaDefinition['schema'] | undefined> {
    if (!ids?.primaryNodeId) {
      return;
    }

    if (ids.primaryNodeId.catalogKind === CatalogKind.Entity && ids.primaryNodeId.name === 'PipeConfiguration') {
      return await this.getRootPipeSchema();
    }

    const definition = await DynamicCatalogRegistry.get().getEntity(
      ids.primaryNodeId.catalogKind,
      ids.primaryNodeId.name,
    );
    return definition?.propertiesSchema;
  }

  getNodeDefinition(path?: string): unknown {
    if (!path) return undefined;
    if (path === this.getRootPath()) {
      return getCustomSchemaFromPipe(this.pipe);
    }

    const stepModel: PipeStep = getValue(this.pipe.spec, path);
    return stepModel?.properties ?? {};
  }

  getOmitFormFields(): string[] {
    return [];
  }

  toJSON() {
    return this.pipe.spec;
  }

  updateModel(path: string | undefined, value: Record<string, unknown>): void {
    if (!path) return;

    if (path === this.getRootPath()) {
      updatePipeFromCustomSchema(this.pipe, value);
      this.id = this.pipe.metadata!.name as string;
      return;
    }

    const stepModel = getValue(this.pipe.spec, path) as PipeStep;
    if (stepModel) setValue(stepModel, 'properties', value);
  }

  /**
   * Add a step to the Pipe
   *
   * path examples:
   *      source
   *      sink
   *      steps.0
   *      steps.1
   */
  addStep(options: {
    definedComponent: DefinedComponent;
    mode: AddStepMode;
    data: IVisualizationNodeData;
    targetProperty?: string;
  }) {
    const newKamelet = options.definedComponent.definition as unknown as Pipe;
    if (!newKamelet) return;

    const step: PipeStep = {
      ref: {
        kind: newKamelet.kind,
        apiVersion: newKamelet.apiVersion,
        name: options.definedComponent.name,
      },
    };

    this.addNewStep(step, options.mode, options.data);
  }

  getCopiedContent(path?: string, ids?: IVisualizationNodeIds): IClipboardContent | undefined {
    if (!path || !ids?.primaryNodeId?.name) return;

    const contentName = ids.primaryNodeId.name;

    // Allow copying the entire pipe entity
    if (path === this.getRootPath() && contentName === 'PipeConfiguration') {
      return { name: 'pipe', definition: this.pipe as object };
    }

    const stepModel: PipeStep = getValue(this.pipe.spec, path);
    return { name: contentName, definition: stepModel as object };
  }

  pasteStep(options: { clipboardContent: IClipboardContent; mode: AddStepMode; data: IVisualizationNodeData }) {
    const definition = options.clipboardContent.definition;
    // Guard: a whole-pipe object has a `spec` property, not a `ref` — reject it
    if (!definition || typeof definition !== 'object' || 'spec' in definition) return;
    this.addNewStep(definition as PipeStep, options.mode, options.data);
  }

  canDragNode(path?: string) {
    if (!isDefined(path)) return false;

    return path !== 'source' && path !== 'sink';
  }

  canDropOnNode(path?: string) {
    return this.canDragNode(path);
  }

  removeStep(path?: string): void {
    /** This method needs to be enabled after passing the entire parent to this class*/
    if (!path) return;
    /**
     * If the path is `source` or `sink`, we can remove it directly
     */
    if (path === 'source' || path === 'sink') {
      setValue(this.pipe.spec, path, {});
      return;
    }
    const pathArray = path.split('.');
    const last = pathArray[pathArray.length - 1];
    /**
     * If the last segment is a number, it means the target object is a member of an array
     * therefore we need to look for the array and remove the element at the given index
     *
     * f.i. from.steps.1.choice.when.0
     * last: 0
     */
    const array = getArrayProperty(this.pipe.spec!, pathArray.slice(0, -1).join('.'));
    if (Number.isInteger(Number(last)) && Array.isArray(array)) {
      array.splice(Number(last), 1);
    }
  }

  getNodeInteraction(data: IVisualizationNodeData): NodeInteraction {
    return {
      /** Pipe cannot have a Kamelet before the source property */
      canHavePreviousStep: data.path !== this.getRootPath() && data.path !== 'source',
      /** Pipe cannot have a Kamelet after the sink property */
      canHaveNextStep: data.path !== this.getRootPath() && data.path !== 'sink',
      canHaveChildren: false,
      canHaveSpecialChildren: false,
      canReplaceStep: data.path !== this.getRootPath(),
      canRemoveStep: data.path !== this.getRootPath(),
      canRemoveFlow: data.path === this.getRootPath(),
      canBeDisabled: false,
    };
  }

  async getNodeValidationText(path?: string, schema?: KaotoSchemaDefinition['schema']): Promise<string | undefined> {
    const definition = this.getNodeDefinition(path);
    if (!schema || !definition) return undefined;

    return await ModelValidationService.validateNodeStatus(schema, definition);
  }

  async toVizNode(): Promise<IVisualizationNode> {
    const pipeGroupNode = createVisualizationNode(this.id, {
      name: this.type,
      path: this.getRootPath(),
      entity: this,
      isPlaceholder: false,
      isGroup: true,
      iconUrl: '',
      title: '',
      description: '',
      processorIconTooltip: '',
      primaryNodeId: { name: 'PipeConfiguration', catalogKind: CatalogKind.Entity } satisfies NodeIdentity,
    });

    const sourceNode = await this.getVizNodeFromStep(this.pipe.spec!.source, 'source', true);
    const stepNodes = await this.getVizNodesFromSteps(this.pipe.spec!.steps);
    const sinkNode = await this.getVizNodeFromStep(this.pipe.spec!.sink, 'sink');

    pipeGroupNode.addChild(sourceNode);
    stepNodes.forEach((stepNode) => {
      pipeGroupNode.addChild(stepNode);
    });
    pipeGroupNode.addChild(sinkNode);

    /** If there are no steps, we link the `source` and the `sink` together */
    if (stepNodes.length === 0) {
      sourceNode.setNextNode(sinkNode);
      sinkNode.setPreviousNode(sourceNode);
      await NodeEnrichmentService.enrichVisualizationTree(pipeGroupNode);
      return pipeGroupNode;
    }

    /** Connect the `source` with the first step */
    const firstStepNode = stepNodes[0];
    if (firstStepNode !== undefined) {
      sourceNode.setNextNode(firstStepNode);
      firstStepNode.setPreviousNode(sourceNode);
    }

    /** Connect the last step with the `sink` */
    const lastStepNode = stepNodes[stepNodes.length - 1];
    if (lastStepNode !== undefined) {
      lastStepNode.setNextNode(sinkNode);
      sinkNode.setPreviousNode(lastStepNode);
    }

    await NodeEnrichmentService.enrichVisualizationTree(pipeGroupNode);

    return pipeGroupNode;
  }

  private addNewStep(step: PipeStep, mode: AddStepMode, data: IVisualizationNodeData) {
    const path = data.path;
    if (!path) return;

    /** Replace an existing Kamelet */
    if (mode === AddStepMode.ReplaceStep) {
      setValue(this.pipe.spec, path, step);
      return;
    }

    /** Add a new Kamelet to the Kamelets array */
    const kameletArray: PipeStep[] = getArrayProperty(this.pipe.spec!, 'steps');
    const index = Number(path.split('.').pop());
    if (mode === AddStepMode.AppendStep) {
      kameletArray.splice(index + 1, 0, step);
    } else if (mode === AddStepMode.PrependStep && data.path === 'sink') {
      kameletArray.push(step);
    } else if (mode === AddStepMode.PrependStep) {
      kameletArray.splice(index, 0, step);
    }
  }

  private async getVizNodeFromStep(step: PipeStep, path: string, isRoot = false): Promise<IVisualizationNode> {
    const kameletName = step?.ref?.name;
    const isPlaceholder = kameletName === undefined;

    const data: IVisualizationNodeData = {
      name: kameletName ?? PlaceholderType.Placeholder,
      path,
      entity: isRoot ? this : undefined,
      isPlaceholder,
      isGroup: false,
      iconUrl: '',
      title: '',
      description: '',
      processorIconTooltip: '',
      ...(kameletName !== undefined
        ? { primaryNodeId: { name: kameletName, catalogKind: CatalogKind.Kamelet } satisfies NodeIdentity }
        : {}),
    };

    const vizNode = createVisualizationNode(path, data);
    return vizNode;
  }

  private async getVizNodesFromSteps(steps: PipeStep[] = []): Promise<IVisualizationNode[]> {
    const vizNodes: IVisualizationNode[] = [];
    for (let index = 0; index < steps.length; index++) {
      const kamelet = steps[index];
      const path = `steps.${index}`;
      const vizNode = await this.getVizNodeFromStep(kamelet, path);

      const previousVizNode = vizNodes[vizNodes.length - 1];
      if (previousVizNode !== undefined) {
        previousVizNode.setNextNode(vizNode);
        vizNode.setPreviousNode(previousVizNode);
      }

      vizNodes.push(vizNode);
    }
    return vizNodes;
  }

  private async getRootPipeSchema(): Promise<KaotoSchemaDefinition['schema']> {
    const rootPipeDefinition = await DynamicCatalogRegistry.get().getEntity(CatalogKind.Entity, 'PipeConfiguration');

    if (rootPipeDefinition === undefined) return {} as unknown as KaotoSchemaDefinition['schema'];

    let schema = {} as unknown as KaotoSchemaDefinition['schema'];
    if (rootPipeDefinition.propertiesSchema !== undefined) {
      schema = rootPipeDefinition.propertiesSchema;
    }

    return schema;
  }
}
